import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { useAuth } from "@/contexts/auth-context";
import { useInviteUser } from "@/hooks/queries/useInviteUser";
import {
  CREATE_USER_ROLE,
  createUserSchema,
  type CreateUserValues,
} from "@/routes/admin/create-user-schema";
import {
  toAdminUserError,
  type AdminUserBanner,
} from "@/routes/admin/admin-user-error";
import type { AdminUsersListRouterState } from "@/routes/admin/AdminUsersListScreen";

// Story 11.9 — the admin create-user form (Screen 22).
//
// FOUR fields. Screen 22 draws seven plus a note; the removals are PDM decisions
// with recorded bases (D2 Password, R1 Access state + Invitation-email hold as ONE
// exception, R2 Initial subscription, R6 Admin note) and the story's divergence map
// is the W3 expected delta. They are ABSENT — not hidden, not disabled — so the
// post-dev mockup diff shows removals and nothing else.
//
// THE TEMPORARY PASSWORD NEVER TOUCHES THIS SCREEN. 11.2 generates it, emails it,
// and returns a 201 that carries no password and no hash by contract; this screen
// renders nothing from that response except the address it went to. That is why
// there is no Password field to restore: FR56c's "set a temporary password when
// support action is required" is the SUPPORT path and lives on Screen 21's Change
// Password action (11.4), not here.
//
// AN ORG_ADMIN WHO REACHES THIS URL SEES A REFUSAL, NOT A FORM. 11.2 denies them
// `invite_platform_user`, so a rendered form could only ever collect a name, an
// address and a click before 403-ing. Their own invite path is /org-admin/add-person
// and the refusal names it. This is Franklin#428's shipped posture
// (`nav-config.ts:52-57`): a control that 403s is not offered.

export function AdminCreateUserScreen() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [banner, setBanner] = React.useState<AdminUserBanner | null>(null);

  const inviteUser = useInviteUser();

  const form = useForm<CreateUserValues>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      role: CREATE_USER_ROLE,
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (inviteUser.isPending) return;
    setBanner(null);
    const email = values.email.trim();
    inviteUser.mutate(
      {
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        email,
        // Always the literal. The schema makes any other value impossible, and
        // 11.2 refuses one with a 422 (boundary #10).
        role: CREATE_USER_ROLE,
      },
      {
        onSuccess: () => {
          // The confirmation lives on the LIST (epics `:2517`), carried across in
          // router state — a banner rendered here would be shown on a screen that
          // is unmounting. The hook has already invalidated the list query, so the
          // new row is there when the list paints.
          const state: AdminUsersListRouterState = { invitedEmail: email };
          navigate("/admin/users", { state });
        },
        onError: (error) => {
          const mapped = toAdminUserError(error);
          // A duplicate address is an INLINE field error and nothing else: no
          // navigation, and every other field keeps its value, so a typo does not
          // cost the admin the whole form.
          if (mapped.fieldErrors.firstName) {
            form.setError("firstName", { message: mapped.fieldErrors.firstName });
          }
          if (mapped.fieldErrors.lastName) {
            form.setError("lastName", { message: mapped.fieldErrors.lastName });
          }
          if (mapped.fieldErrors.email) {
            form.setError("email", { message: mapped.fieldErrors.email });
          }
          setBanner(mapped.banner);
        },
      },
    );
  });

  if (!hasRole("super_admin")) {
    return (
      <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-6">
        <h1 className="display-xs text-[color:var(--text-primary)]">Create user</h1>
        <Banner variant="danger" title="Platform invitations are a super admin action">
          <div className="flex flex-col gap-2">
            <span>
              You cannot invite platform users. To add a clinician to your organization,
              use Add person on the seats screen — that invitation consumes one of your
              organization's seats and keeps the clinician in your roster.
            </span>
            <span className="flex flex-wrap gap-3">
              <Button asChild variant="secondary" size="sm">
                <Link to="/admin/users">Back to users</Link>
              </Button>
              <Button asChild variant="link" size="sm">
                <Link to="/org-admin/add-person">Add person to your organization</Link>
              </Button>
            </span>
          </div>
        </Banner>
      </section>
    );
  }

  return (
    <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="display-xs text-[color:var(--text-primary)]">Create user</h1>
          {/* The mockup's sub-line promises "or admin account and define its initial
              access state"; both halves are reworded, because this form mints
              clinicians (R4) and defines no access state (R1). */}
          <p className="text-sm text-[color:var(--text-tertiary)]">
            Invite an individual clinician onto the platform. The account is created
            active and an invitation email carries a temporary password, which they are
            asked to change on their first sign-in.
          </p>
        </div>
        <div className="shrink-0">
          <Button asChild variant="secondary">
            <Link to="/admin/users">Back to users</Link>
          </Button>
        </div>
      </header>

      {banner ? (
        <Banner variant={banner.variant} title={banner.title}>
          <div className="flex flex-col gap-2">
            <span>{banner.body}</span>
            {banner.code ? <span>{`Reference code: ${banner.code}`}</span> : null}
          </div>
        </Banner>
      ) : null}

      <form className="flex flex-col gap-6" onSubmit={onSubmit} noValidate>
        {/* The mockup's `field-grid cols-3` is kept with four fields (boundary #3):
            a 3+1 grid is the mockup's own layout minus the removed controls, so the
            W3 diff reads as removals rather than a re-flow. */}
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label="First name" error={form.formState.errors.firstName?.message} required>
            <Input autoComplete="off" placeholder="Given name" {...form.register("firstName")} />
          </FormField>
          <FormField label="Last name" error={form.formState.errors.lastName?.message} required>
            <Input autoComplete="off" placeholder="Family name" {...form.register("lastName")} />
          </FormField>
          {/* One option, and the helper line says why rather than leaving the admin
              to wonder what the control is for. The mockup's "Admin" option is
              removed: creating an org admin requires naming an organization (and
              raises the seat question), which is organization provisioning. */}
          <FormField
            label="Role"
            htmlFor="create-user-role"
            helper="Organization administrators are created with their organization."
            required
          >
            <Controller
              name="role"
              control={form.control}
              render={({ field }) => (
                <SearchableCombobox
                  id="create-user-role"
                  options={[{ value: CREATE_USER_ROLE, label: "Clinician" }]}
                  value={field.value}
                  onChange={field.onChange}
                  searchAriaLabel="User role"
                  showSearch={false}
                />
              )}
            />
          </FormField>
          <FormField label="Email" error={form.formState.errors.email?.message} required>
            <Input
              type="email"
              autoComplete="off"
              placeholder="name@example.org"
              {...form.register("email")}
            />
          </FormField>
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-3">
          <Button type="submit" variant="primary" disabled={inviteUser.isPending}>
            {inviteUser.isPending ? "Sending…" : "Create user"}
          </Button>
          <Button asChild variant="link">
            <Link to="/admin/users">Cancel</Link>
          </Button>
        </div>
      </form>
    </section>
  );
}

export default AdminCreateUserScreen;
